"use client";
import React, { useState, useRef } from "react";
// import toast from "react-hot-toast";
import { parseError } from "../../../utils/errors";
import { SendHorizonalIcon } from "lucide-react";
import { useSubmitContactFormWithMutation } from "@/queries/communication/SubmitcontactForm";
// import ReCAPTCHA from "react-google-recaptcha";
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Card,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

// List of disallowed personal email domains
const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'protonmail.com',
  'mail.com',
  'zoho.com',
  'live.com',
  'me.com',
  'inbox.com',
  'gmx.com'
];

export default function ContactWithImageCover() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const recaptcha_site_key = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
  const { toast } = useToast();
  const { mutate: submitContactForm } = useSubmitContactFormWithMutation({
    onSuccess: async (message) => {
      // toast.success(message);
      toast({
        title: "Success",
        description: message,
      });

      setFirstName(" ");
      setLastName(" ");
      setCompany(" ");
      setEmail(" ");
      setMessage(" ");
    },
    onError: (error: any) => {
      // toast.error(parseError(error));
      toast({
        variant: "destructive",
        title: "Error",
        description: parseError(error),
      });

      setFirstName(" ");
      setLastName(" ");
      setCompany(" ");
      setEmail(" ");
      setMessage(" ");
    },
  });
  // const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleContact = async (e: { preventDefault: () => void }) => {
    // Validation logic
    let token = null;
    // if (recaptcha_site_key) {
    //   token = await recaptchaRef.current?.executeAsync();
    // }

    // console.log(token);

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    // Company email validation
    const emailDomain = email.split('@')[1].toLowerCase();
    if (PERSONAL_EMAIL_DOMAINS.includes(emailDomain)) {
      toast({
        variant: "destructive",
        title: "Business Email Required",
        description: "Please use your company email address instead of a personal email.",
      });
      return;
    }

    // Company field validation
    if (!company.trim()) {
      toast({
        variant: "destructive",
        title: "Company Required",
        description: "Please enter your company name.",
      });
      return;
    }

    // Add company to message as fallback in case backend doesn't support company field
    const enhancedMessage = `Company: ${company}\n\n${message}`;

    submitContactForm({
      first_name: firstName,
      last_name: lastName,
      company: company,
      email: email,
      message: enhancedMessage,
      token: (token ?? '') as string,
    });
  };
  return (
    <>
      {/* Contact Section: With Image Cover */}
      <div
        className="bg-cover bg-center"
        style={{
          backgroundImage: 'url("/images/pharma-lab-blur.jpg")',
        }}
      >
        <div className="bg-gray-50/95 dark:bg-gray-900/95 dark:text-gray-100">
          <div className="container mx-auto px-4 py-16 lg:px-8 lg:py-32 xl:max-w-7xl">
            <div className="flex flex-col space-y-16 lg:flex-row lg:items-center lg:space-x-16 lg:space-y-0">
              {/* Heading with Company Info */}
              <div className="lg:w-2/5">
                <div className="mb-1 text-sm font-bold uppercase tracking-wider text-blue-600 dark:text-blue-500">
                  We respond within 24 hours
                </div>
                <h2 className="mb-4 text-4xl font-black text-black dark:text-white">
                  Get in touch
                </h2>
                <h3 className="text-xl font-medium leading-relaxed text-gray-700 dark:text-gray-300">
                  Interested in transforming your evidence planning process? Contact us to discover how EvidenceSync can help your organization.
                </h3>
                <h4 className="mb-4 mt-10 font-bold tracking-wider">
                  EvidenceSync
                </h4>
                <div className="leading-relaxed text-gray-600 dark:text-gray-400">
                  Miami Beach, FL 33140<br />
                  <abbr title="Phone">P:</abbr> +1 (305) 317-2134<br />
                  <abbr title="Email">E:</abbr> info@evidencesync.ai
                </div>
              </div>
              {/* END Heading with Company Info */}

              {/* Contact Form */}
              <Card className="flex flex-col overflow-hidden bg-white shadow-sm dark:bg-gray-800 dark:text-gray-100 lg:mx-auto lg:w-1/2">
                <div className="space-y-6 p-5 md:p-10">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="firstname" className="font-medium">
                        First Name
                      </Label>
                      <Input
                        type="text"
                        id="firstname"
                        name="firstname"
                        required={true}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className=""
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="lastname" className="font-medium">
                        Last Name
                      </Label>
                      <Input
                        type="text"
                        id="lastname"
                        name="lastname"
                        value={lastName}
                        required={false}
                        onChange={(e) => setLastName(e.target.value)}
                        className=""
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="company" className="font-medium">
                      Company
                    </Label>
                    <Input
                      type="text"
                      id="company"
                      name="company"
                      required={true}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className=""
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="email" className="font-medium">
                      Business Email
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      required={true}
                      value={email}
                      onChange={(e) => {
                        return setEmail(e.target.value);
                      }}
                      className=""
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="message" className="font-medium">
                      Message
                    </Label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={message}
                      required={true}
                      onChange={(e) => setMessage(e.target.value)}
                      className="block w-full rounded-lg border border-gray-200 px-5 py-3 leading-6 placeholder-gray-500 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 dark:focus:border-blue-500"
                    />
                  </div>
                  <Button
                    type="submit"
                    onClick={handleContact}
                    disabled={!firstName || !company || !email || !message}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <SendHorizonalIcon className="hi-mini hi-paper-airplane inline-block h-5 w-5 opacity-50" />
                    <span>Send Message</span>
                  </Button>
                  {process.env.NODE_ENV === "development" ? (
                    <div className="mt-4 flex flex-col items-center">
                      <div className="w-full max-w-xs rounded-md border border-red-400 bg-red-50 p-3 flex items-center gap-2">
                        <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.054 0 1.918-.816 1.995-1.85l.007-.15V6c0-1.054-.816-1.918-1.85-1.995L19.856 4H6.144C5.09 4 4.226 4.816 4.15 5.85l-.007.15v12c0 1.054.816 1.918 1.85 1.995l.15.005z" />
                        </svg>
                        <span className="text-sm text-red-700 font-medium">
                          reCAPTCHA is disabled on localhost.<br />
                          To test this form, use a valid site key and add localhost to allowed domains in your reCAPTCHA settings.
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
              {/* END Contact Form */}
            </div>
          </div>
          {/* END Contact Section: With Image Cover */}
        </div>
      </div>
      {/* END Contact Section: With Image Cover */}
    </>
  );
}
